import React, { FC, useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Checkbox, FormControlLabel, Grid, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { PasswordInput } from "@/components/molecules";
import { TermsOfUseDialog } from "@/components/organisms";

import { PasswordFormType, passwordValidationSchema } from "../../../shared";

type RegisterDefinePasswordProps = {
    isFetching: boolean;
    onDefinePassword: (password: string) => void;
};

const defaultValues = { password: "" };

const RegisterDefinePassword: FC<RegisterDefinePasswordProps> = ({ onDefinePassword, isFetching }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        handleSubmit,
        control,
        setValue,
        formState: { isValid, isSubmitting },
    } = useForm<PasswordFormType>({
        defaultValues,
        resolver: yupResolver(passwordValidationSchema),
    });
    const [termsConfirmed, setTermsConfirmed] = useState(false);

    const onSubmit = (data: PasswordFormType) => {
        onDefinePassword(data.password);
    };

    const [termsState, setTermsState] = useState(false);

    const onClickTerms = () => {
        setTermsState(true);
    };

    const handleCloseTerms = () => {
        setTermsState(false);
    };

    return (
        <>
            {termsState && <TermsOfUseDialog onClose={handleCloseTerms} />}
            <div className="title">{t("Registration.DefinePassword.Title")}</div>
            <div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container direction="column">
                        <Grid item>
                            <PasswordInput
                                control={control}
                                label={t("Registration.DefinePassword.YourPassword")}
                                required
                                name="password"
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setValue("password", event.target.value, { shouldValidate: true });
                                }}
                                isNew
                                placeholder={t("ResetPassword.NewPassword.Placeholder")}
                            />
                        </Grid>
                        <Grid item>
                            <FormControlLabel
                                style={{ alignItems: "baseline", marginTop: isMobileScreen ? "" : "2rem" }}
                                name="terms"
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={termsConfirmed}
                                        inputProps={{
                                            // Necessary, waiting https://github.com/mui/material-ui/issues/34154 than remove
                                            // @ts-ignore
                                            "data-testid": "register_password-checkbox-termsDialog",
                                        }}
                                        onChange={() => setTermsConfirmed(!termsConfirmed)}
                                    />
                                }
                                label={
                                    <div className="form-label">
                                        <div style={{ margin: 0 }}>
                                            {t("Registration.DefinePassword.AcceptThe")}{" "}
                                            <span
                                                className="hyperlink"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    onClickTerms();
                                                }}
                                            >
                                                {t("Registration.DefinePassword.Terms")}
                                            </span>
                                        </div>
                                    </div>
                                }
                                labelPlacement="end"
                            />
                        </Grid>

                        <div className="form-bottom-container">
                            <Button
                                type="submit"
                                data-testid="submit-button"
                                fullWidth
                                disabled={!termsConfirmed || isFetching || isSubmitting || !isValid}
                            >
                                {t("Registration.DefinePassword.CreateAccount")}
                            </Button>
                        </div>
                    </Grid>
                </form>
            </div>
        </>
    );
};

export default RegisterDefinePassword;
